import { StateCreator } from 'zustand';

import { diff } from 'datum-diff';

import { FilterFunc, PlanSlice } from '@/types/stores/plan';
import { BuildSlice } from '@/types/stores/build';

import { PlannerSlice } from '@/types/stores/planner';
import { ItemCreateOpts, ProjectSlice } from '@/types/stores/project';
import { RootActions } from '@/types/stores/rootActions';

import { scaleAssetForPlanner, scaleAssetForStore } from '@/common/planningUtils';

import { planAction } from './storeCommon';
import { InvalidStateError } from '@/common/errors';
import i18n from '@/i18n';
import {
  Device,
  Hood,
  Appliance,
  Group,
  Room,
  VAV,
  OptionalTuple,
  Comment,
  FloatingAppliance,
  Measurement,
  Diffuser,
  DraggableDeviceTypeName,
  DraggableDevice,
  DraggableDeviceOfType,
  Zone,
} from '@/types/helpers';
import { ApplianceId, GroupId, HoodId } from '@/api/airchef/Api';

import { airchefApi, pimApi } from '@/api';
import { createMeasurement } from '@/common/measurements';
import { createEmptyGroup, GroupUpdater, RowBuilder, rowIsEmpty } from '@/common/itemUtils';
import { assertDeprecated, generateRoomPartId, getCurrentTimestamp, simpleClone } from '@/common/utils';

import { createRoom, createVav, createZone } from '@/common/projectItems';

import { FeatureFlags } from '@/common/FeatureFlags';
import { createHoodAtAppliance as createHoodAtApplianceHelper } from './plan/createHoodAtAppliance';
import { updateAssetModified, updateProjectModified } from './project/helpers';

const BATCH_WAIT_MS = 10;

export const createPlanSlice: StateCreator<
  BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  PlanSlice
> = (set, get) => ({
  plan: {
    availableAppliances: {
      list: [],
      loading: false,
      setLoading: (loading) => {
        set(
          (state) => {
            state.plan.availableAppliances.loading = loading;
          },
          undefined,
          {
            type: 'plan/available/appliances/setLoading',
            loading,
          },
        );
      },
      load: async (force) => {
        const { setLoading, loading, list } = get().plan.availableAppliances;
        if (loading) {
          return;
        }
        if (list && list.length > 0 && !force) return;
        setLoading(true);
        try {
          const response = await airchefApi.appliance.appliancesList({
            schemaPath: get().project.projectSchemaVersion,
          });

          const data = response.data.appliances;

          if (!data || !Array.isArray(data)) {
            return;
          }

          const filteredData = FeatureFlags.filterDraftDevices ? data.filter(filterDraftDevices) : data;

          set(
            (state) => {
              state.plan.availableAppliances.list = filteredData;
            },
            undefined,
            { type: 'plan/available/appliances/load', data },
          );
        } catch (err) {
          console.log(err);
          throw err;
        } finally {
          setLoading(false);
        }
      },
    },
    availableHoods: {
      list: [],
      loading: false,
      setLoading: (loading) => {
        set(
          (state) => {
            state.plan.availableHoods.loading = loading;
          },
          undefined,
          {
            type: 'plan/available/hoods/setLoading',
            loading,
          },
        );
      },
      load: async (force) => {
        const { setLoading, loading, list } = get().plan.availableHoods;
        if (loading) {
          return;
        }
        if (list && list.length > 0 && !force) return;
        setLoading(true);
        try {
          const { data } = (await pimApi.hood.hoodsList({
            schemaPath: get().project.projectSchemaVersion,
          })) as unknown as { data: Hood[] };

          set(
            (state) => {
              state.plan.availableHoods.list = FeatureFlags.filterDraftDevices ? data.filter(filterDraftDevices) : data;
            },
            undefined,
            { type: 'plan/available/hoods/load', data },
          );
        } catch (err) {
          console.log(err);
          throw err;
        } finally {
          setLoading(false);
        }
      },
    },
    availableDiffusers: {
      list: [],
      loading: false,
      setLoading: (loading) => {
        set(
          (state) => {
            state.plan.availableDiffusers.loading = loading;
          },
          undefined,
          {
            type: 'plan/available/diffusers/setLoading',
            loading,
          },
        );
      },
      load: async (force) => {
        const { setLoading, loading, list } = get().plan.availableDiffusers;
        if (loading) {
          return;
        }
        if (list && list.length > 0 && !force) return;
        setLoading(true);
        try {
          const { data } = (await pimApi.diffuser.diffusersList({
            schemaPath: get().project.projectSchemaVersion,
          })) as { data: Diffuser[] };
          set(
            (state) => {
              state.plan.availableDiffusers.list = data;
            },
            undefined,
            { type: 'plan/available/diffusers/load', data },
          );
        } catch (err) {
          console.log(err);
          throw err;
        } finally {
          setLoading(false);
        }
      },
    },
    availableCeilings: {
      list: [],
      loading: false,
      setLoading: (loading) => {
        set(
          (state) => {
            state.plan.availableCeilings.loading = loading;
          },
          undefined,
          {
            type: 'plan/available/ceilings/setLoading',
            loading,
          },
        );
      },
      load: async (force) => {
        const { setLoading, loading, list } = get().plan.availableCeilings;
        if (loading) {
          return;
        }
        if (list && list.length > 0 && !force) return;
        setLoading(true);
        try {
          const { data } = await pimApi.ceiling.ceilingsList({
            schemaPath: get().project.projectSchemaVersion,
          });
          set(
            (state) => {
              state.plan.availableCeilings.list = data;
            },
            undefined,
            { type: 'plan/available/ceilings/load', data },
          );
        } catch (err) {
          console.log(err);
          throw err;
        } finally {
          setLoading(false);
        }
      },
    },
    availableGroup: {
      base: null,
      get: async (force) => {
        const { plan } = get();
        if (plan.availableGroup.base && !force) {
          return generateGroupIds(plan.availableGroup.base);
        }

        let response: Awaited<ReturnType<typeof airchefApi.groups.groupsList>> | null = null;
        try {
          response = await airchefApi.groups.groupsList({
            schemaPath: get().project.projectSchemaVersion,
          });
        } catch (err) {
          console.error('Failed to load group data from backend, using empty placeholder', err);
        }

        const emptyGroup: Group = response?.data
          ? (response.data as Group)
          : createEmptyGroup(get().project.projectVersion);
        set(
          (state) => {
            state.plan.availableGroup.base = emptyGroup;
          },
          undefined,
          { type: 'plan/available/group/get' },
        );

        return generateGroupIds(emptyGroup);
      },
    },
    availableZone: {
      base: null,
      async get(force) {
        const { plan } = get();
        if (plan.availableZone.base && !force) {
          const clone = simpleClone(plan.availableZone.base);
          clone.id = generateRoomPartId();
          return clone;
        }

        let response: Awaited<ReturnType<typeof airchefApi.zones.zonesList>> | null = null;
        try {
          response = await airchefApi.zones.zonesList({
            schemaPath: get().project.projectSchemaVersion,
          });
        } catch (err) {
          console.error('Failed to load zone data from backend, using empty placeholder', err);
        }

        const emptyZone: Zone = response?.data ? (response.data as Zone) : createZone(get().project.projectVersion);

        set(
          (state) => {
            state.plan.availableZone.base = emptyZone;
          },
          undefined,
          { type: 'plan/available/zone/get' },
        );

        const clone = simpleClone(emptyZone);
        clone.id = generateRoomPartId();
        return clone;
      },
      async quickCreate() {
        const newZone = await get().plan.availableZone.get();

        const zoneCount = Object.keys(get().project.data.zones).length;
        const now = getCurrentTimestamp();
        set(
          (state) => {
            state.project.data.zones[newZone.id] = newZone;
            state.project.data.zones[newZone.id].airchefValues.name = i18n.t('zone.placeholder.name', {
              ord: zoneCount + 1,
              defaultValue: 'Zone {{ ord }}',
            });
            state.project.data.zones[newZone.id].airchefValues.datesAndAudit = {
              author: state.planner.user.data?.userPrincipalName ?? '',
              modifiedBy: state.planner.user.data?.userPrincipalName ?? '',
              createdAt: now,
              modifiedAt: now,
            };
          },
          undefined,
          { type: 'plan/available/zone/quickCreate' },
        );

        return get().project.data.zones[newZone.id];
      },
    },
    availableRoom: {
      base: null,
      get: async (force) => {
        const { plan } = get();
        if (plan.availableRoom.base && !force) {
          const clone = simpleClone(plan.availableRoom.base);
          clone.id = generateRoomPartId();
          return clone;
        }

        let response: Awaited<ReturnType<typeof airchefApi.room.roomsList>> | null = null;
        try {
          response = await airchefApi.room.roomsList({ schemaPath: get().project.projectSchemaVersion });
        } catch (err) {
          console.error('Failed to load room data from backend, using empty placeholder', err);
        }

        const emptyRoom: Room = (response?.data as Room | undefined) ?? createRoom(get().project.projectVersion);

        set(
          (state) => {
            state.plan.availableRoom.base = emptyRoom;
          },
          undefined,
          { type: 'plan/available/room/get' },
        );
        const clone = simpleClone(emptyRoom);
        clone.id = generateRoomPartId();
        return clone;
      },
    },
    availableSupplies: {
      list: [],
      get: async () => {
        let response: Awaited<ReturnType<typeof pimApi.supply.suppliesList>> | null = null;
        try {
          response = await pimApi.supply.suppliesList({ schemaPath: get().project.projectSchemaVersion });
          if (!response?.data) {
            throw new InvalidStateError('Got no data from supplies endpoint!');
          }
        } catch (err) {
          console.error('Failed to load supply data', err);
          return;
        }

        set(
          (state) => {
            // @ts-expect-error haltonValues.type is just string in api response, more strict in ui
            state.plan.availableSupplies.list = response.data;
          },
          undefined,
          { type: 'plan/available/supplies/get' },
        );
      },
    },
    availableExhausts: {
      list: [],
      get: async () => {
        let response: Awaited<ReturnType<typeof pimApi.exhaust.exhaustsList>> | null = null;
        try {
          response = await pimApi.exhaust.exhaustsList({ schemaPath: get().project.projectSchemaVersion });
          if (!response?.data) {
            throw new InvalidStateError('Got no data from exhausts endpoint!');
          }
        } catch (err) {
          console.error('Failed to load exhaust data', err);
          return;
        }

        set(
          (state) => {
            // @ts-expect-error haltonValues.type is just string in api response, more strict in ui
            state.plan.availableExhausts.list = response.data;
          },
          undefined,
          { type: 'plan/available/exhausts/get' },
        );
      },
    },
    availableVAVs: {
      list: [],
      get: (async (name, type) => {
        const { plan } = get();
        function getVavName() {
          const count = Object.keys(get().project.data.vavs).length;
          return `VAV ${count + 1}`;
        }
        if (name !== undefined && type !== undefined) {
          const foundType = plan.availableVAVs.list.find((s) => s.haltonValues.type === type);
          if (foundType) {
            const clone = simpleClone(foundType);
            clone.id = generateRoomPartId();
            clone.airchefValues.name ||= name || getVavName();
            return clone;
          }
        }

        let response: Awaited<ReturnType<typeof pimApi.vav.vavsList>> | null = null;
        try {
          response = await pimApi.vav.vavsList({ schemaPath: get().project.projectSchemaVersion });
          if (!response?.data) {
            throw new InvalidStateError('Got no data from vavs endpoint!');
          }
        } catch (err) {
          console.error('Failed to load vav data, using empty placeholder', err);
          if (name !== undefined && type !== undefined) return createVav(name, get().project.projectVersion, type);
        }

        set(
          (state) => {
            // @ts-expect-error haltonValues.type is just string in api response, more strict in ui
            state.plan.availableVAVs.list = response?.data ?? [];
          },
          undefined,
          { type: 'plan/available/vavs/get' },
        );

        if (name === undefined || type === undefined) return;

        const found = get().plan.availableVAVs.list.find((s) => s.haltonValues.type === type);
        if (!found) {
          // ???
          console.error(`Missing VAV type ${type} after loading data from backend, using empty one.`);
          return createVav(name || getVavName(), get().project.projectVersion, type);
        }
        const clone = simpleClone(found);
        clone.id = generateRoomPartId();
        clone.airchefValues.name ||= name || getVavName();
        return clone;
      }) as {
        (name: undefined, type: undefined): Promise<void>;
        (name: string, type: 'vav-na'): Promise<VAV>;
      },
    },
    loadPimItems: async () => {
      const { availableHoods, availableExhausts, availableSupplies, availableVAVs } = get().plan;

      await Promise.all([availableHoods.load(), availableExhausts.get(), availableSupplies.get(), availableVAVs.get()]);
    },
    actionInProgress: 0,
    dirty: false,
    undoInProgress: false,
    sessionHistory: [],
    /**
     * Returns the assets in the current room, optionally filtered by type.
     * Also scales the devices for drawing.
     *
     * Either scale them back when storing, or use storing functions in this store
     * that does the scaling.
     * @param type
     * @returns
     */
    getAssets: <T extends DraggableDeviceTypeName | never, U extends DraggableDeviceTypeName | never>(
      type?: T | FilterFunc<T | U>,
      type2?: U | FilterFunc<T | U>,
      filterFunc?: FilterFunc<T | U>,
    ) => {
      const { getAssets } = get().project;
      const { currentRoom } = get().planner;

      if (!currentRoom) {
        return [];
      }

      const assets = getAssets();

      if (typeof type === 'function') {
        filterFunc = type;
        type = undefined;
      }
      if (typeof type2 === 'function') {
        filterFunc = type2;
        type2 = undefined;
      }

      const assetsInRoom = assets.filter((a) => a.relations?.roomId === currentRoom);

      if (!type && !type2) {
        // @ts-expect-error no matter here, we care about the signature of getAssets()
        const filteredAssets = filterFunc ? assetsInRoom.filter(filterFunc) : assetsInRoom;
        const scaledAssets = filteredAssets.map(scaleAssetForPlanner);
        return scaledAssets;
      }

      const typedList: DraggableDevice[] = [];

      if (type) {
        typedList.push(...assetsInRoom.filter((a) => a.type === type));
      }

      if (type2) {
        typedList.push(...assetsInRoom.filter((a) => a.type === type2));
      }
      // @ts-expect-error no matter here, we care about the signature of getAssets()
      const filteredAssets = filterFunc ? typedList.filter(filterFunc) : typedList;
      const scaledAssets = filteredAssets.map(scaleAssetForPlanner);

      return scaledAssets;
    },
    getAsset: <T extends DraggableDeviceTypeName | never>(idOrAsset: string | DraggableDeviceOfType<T>, type?: T) => {
      const assetId = typeof idOrAsset === 'string' ? idOrAsset : idOrAsset.id;

      const asset = get().project.getAsset(assetId as HoodId);
      if (asset && (!type || asset.type === type)) {
        return scaleAssetForPlanner(asset) as T extends never ? DraggableDevice : DraggableDeviceOfType<T>;
      }
      return undefined;
    },
    /**
     *
     * @param type
     * @param id
     * @param x
     * @param y
     * @param group
     * @param hood
     */
    createAsset: async (
      type,
      id,
      x?,
      y?,
      groupWithAppliance?: ApplianceId,
      hood?: HoodId,
      offsetMultiplier?: number,
    ) => {
      const { project, planner, plan } = get();

      if (planner.imageRendering) return;

      if (type !== 'appliance' && type !== 'diffuser' && type !== 'ceiling') {
        console.warn(`Tried to create an asset with type ${type}`);
        return;
      }

      plan.startBatchAction('createAsset');
      const opts: ItemCreateOpts<typeof type> = {
        inRoom: true,
        type,
        itemId: id,
        roomId: planner.currentRoom,
        x: (x ?? 0) * 10,
        y: (y ?? 0) * 10,
        offsetMultiplier,
      };

      const created = project.createAsset(opts, planAction, 'createAsset');

      try {
        if (groupWithAppliance && created?.type === 'appliance') {
          // Dragged on to another appliance
          await plan.groupAppliances(created.id, groupWithAppliance, true);
        } else if (created && !groupWithAppliance && hood && created.type === 'appliance') {
          // Dragged directly to hood
          assertDeprecated('createAsset() with hood should not be used');
          await project.addToHood(created, hood, planAction, 'createAsset');
        }
      } catch (err) {
        console.error('Grouping created asset failed', err);
      } finally {
        // groupAppliances & addToHood can trigger updates to the group update logic.
        // Let them complete first with this timeout, and then clear the batch status.
        plan.endBatchAction('createAsset');
      }
    },
    updateAsset: (type, asset, skipHistory) => {
      const { project, plan, planner } = get();

      if (plan.undoInProgress) return;
      if (planner.imageRendering) return;

      const oldAsset = project.getAsset(asset.id);

      if (!oldAsset) {
        console.warn(`updateAsset: asset with id ${asset.id} and type ${type} not found`);
        return;
      }
      if (oldAsset.type !== type) {
        console.warn(`updateAsset type mismatch: expected ${type}, got ${asset.type}`);
        return;
      }
      plan.startBatchAction('updateAsset');
      const scaledAsset = scaleAssetForStore(asset);
      project.updateAsset(scaledAsset, planAction, 'updateAsset');

      plan.endBatchAction('updateAsset', skipHistory);
    },
    updateAssets(assets) {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;

      plan.startBatchAction('updateAssets');

      project.updateAssets(assets.map(scaleAssetForStore), planAction, 'updateAssets');

      plan.endBatchAction('updateAssets');
    },
    groupAppliances: async (
      appliance: Appliance | ApplianceId,
      targetAppliance: Appliance | ApplianceId,
      skipHistory = false,
      beforeOrAfter = 'after',
      secondRow = false,
    ) => {
      const { project, plan, planner } = get();

      if (planner.imageRendering) return;

      const applianceAsset = simpleClone(plan.getAsset(appliance, 'appliance'));

      if (!applianceAsset?.relations.roomId) {
        throw new InvalidStateError('Cannot group appliances that are not in rooms');
      }

      const targetApplianceId = typeof targetAppliance === 'string' ? targetAppliance : targetAppliance.id;
      const targetApplianceAsset = simpleClone(project.getAsset(targetAppliance));

      if (!targetApplianceAsset) {
        console.warn(`groupAppliances: target appliance with id ${targetApplianceId} not found`);
        return;
      }

      if (targetApplianceAsset.id === applianceAsset.id) {
        console.error('Tried to group appliance with itself!');
        return;
      }

      plan.startBatchAction('groupAppliances');

      const sourceGroupId = applianceAsset.relations.groupId;
      const targetGroupId = targetApplianceAsset.relations.groupId;

      const groupAlreadyExists = !!(targetGroupId && project.data.groups[targetGroupId]);
      const baseGroup = await plan.availableGroup.get();
      let targetGroup: Group = groupAlreadyExists
        ? (simpleClone(project.data.groups[targetGroupId]) as Group)
        : new GroupUpdater(baseGroup)
            .setGenerateId(true)
            .setRow(0, [targetApplianceId])
            .setRoomId(applianceAsset.relations.roomId)
            .setPosition(
              targetApplianceAsset.airchefValues.offsetTransform?.position ??
                targetApplianceAsset.airchefValues.transform.position,
            )
            .setRotation(targetApplianceAsset.airchefValues.transform.rotation.x)
            .update();

      if (!groupAlreadyExists) {
        // Update the row data to the target appliance, as the group was just created
        targetApplianceAsset.relations.rowNum = 0;
        targetApplianceAsset.relations.rowId = targetGroup.rows[0].id;
      }

      let otherGroupAppliances: Appliance[] = [];
      const otherGroupHoods: Hood[] = [];
      let deleteSourceHoods = true;
      // Both appliances are already in a group, merge them together by removing the group the moved appliance is in
      if (sourceGroupId === targetGroup.id) {
        console.warn('Tried to add appliance to the group it is already in!');
      } else if (sourceGroupId) {
        // Merging two groups together
        // If both groups have one row, allow merging into two row group
        const sourceGroup = project.data.groups[sourceGroupId];
        const sourceHasOneRow = sourceGroup.rows.length === 1;
        const targetHasOneRow = targetGroup.rows.length === 1;

        if (sourceHasOneRow && targetHasOneRow) {
          const sourceLocked = sourceGroup.airchefValues.hasVirtualWall === true;
          const targetLocked = targetGroup.airchefValues.hasVirtualWall === true;
          // Keep as separate group if target or source group has virtual wall
          if (secondRow && (sourceLocked || targetLocked)) {
            await plan.endBatchAction('groupAppliances', skipHistory);
            return;
          }
          const sourceHoodIds =
            sourceGroup.rows[0].relations.hoodIds.length > 0 && targetGroup.rows[0].relations.hoodIds.length === 0
              ? sourceGroup.rows[0].relations.hoodIds
              : undefined;

          targetGroup = new GroupUpdater(targetGroup)
            .setRow(1, sourceGroup.rows[0].relations.applianceIds, sourceHoodIds)
            .update();

          otherGroupAppliances = project
            .getAssets()
            .filter((a) => a.type === 'appliance')
            .filter((a) => sourceGroup.relations.applianceIds.includes(a.id));

          if (otherGroupAppliances.length !== sourceGroup.relations.applianceIds.length) {
            console.warn(
              `Could not find all grouped assets in project! Expected ${sourceGroup.relations.applianceIds.length} assets, got ${otherGroupAppliances.length}`,
            );
          }

          if (!targetGroup.rows[1]) {
            throw new InvalidStateError('No row [1] after adding it');
          }

          // Map the ids from the source group to the filtered assets
          otherGroupAppliances = sourceGroup.relations.applianceIds.map((aid) => {
            const ogAsset = simpleClone(otherGroupAppliances.find((oa) => oa.id === aid)!);
            ogAsset.relations.groupId = targetGroup.id;
            ogAsset.relations.rowId = targetGroup.rows[1]!.id;
            ogAsset.relations.rowNum = 1;
            return ogAsset;
          });

          if (sourceHoodIds) {
            deleteSourceHoods = false;
            const hoodsToMove = sourceHoodIds
              .map((hid) => {
                const h = simpleClone(project.data.hoods[hid]);
                if (h) {
                  h.relations.groupId = targetGroup.id;
                  h.relations.rowId = targetGroup.rows[1]!.id;
                  h.relations.rowNum = 1;
                  return h;
                }
              })
              .filter((h) => !!h);
            otherGroupHoods.push(...hoodsToMove);
          }
        }

        // This also removes any hoods linked to the source group
        project.removeGroup(sourceGroupId, false, deleteSourceHoods, planAction);
      } else {
        // Adding a single appliance to a group
        if (targetApplianceAsset.relations.rowNum !== 0 && targetApplianceAsset.relations.rowNum !== 1) {
          throw new InvalidStateError(
            `Not supported row num from target appliance! [${targetApplianceAsset.relations.rowNum}]`,
          );
        }
        const floatingAppliance = applianceAsset.airchefValues.transform.position.z.value > 0;
        const rowNum = targetApplianceAsset.relations.rowNum;
        if (rowNum === 0 && secondRow && !floatingAppliance) {
          const targetLocked = targetGroup.airchefValues.hasVirtualWall === true;
          if (targetLocked) {
            // Keep as separate group if target group has virtual wall
            await plan.endBatchAction('groupAppliances', skipHistory);
            return;
          }
          // Target group has one row, and the new appliance should be added to a new row
          targetGroup = new GroupUpdater(targetGroup)
            .setRow(1, new RowBuilder().addAppliance(applianceAsset.id).build())
            .update();
          if (!targetGroup.rows[1]) {
            throw new InvalidStateError('Missing row after creating it!');
          }
          applianceAsset.relations.rowNum = 1;
          applianceAsset.relations.rowId = targetGroup.rows[1].id;
        } else {
          // Add the appliance to the same row

          const updatedRow = floatingAppliance
            ? new RowBuilder(targetGroup.rows[rowNum]).addFloatingAppliance(applianceAsset.id).build()
            : new RowBuilder(targetGroup.rows[rowNum])
                .addAppliance(applianceAsset.id, targetApplianceId, beforeOrAfter)
                .build();

          targetGroup = new GroupUpdater(targetGroup).setRow(rowNum, updatedRow).update();
          if (!targetGroup.rows[rowNum]) {
            throw new InvalidStateError('Missing row after creating it!');
          }
          applianceAsset.relations.rowNum = rowNum;
          applianceAsset.relations.rowId = targetGroup.rows[rowNum].id;
        }
      }

      applianceAsset.relations.groupId = targetGroup.id;
      targetApplianceAsset.relations.groupId = targetGroup.id;

      const assets = [
        scaleAssetForStore(applianceAsset),
        targetApplianceAsset,
        ...otherGroupAppliances,
        ...otherGroupHoods,
      ];

      project.updateAssets(assets, planAction, 'groupAppliances');
      project.updateGroup(targetGroup, planAction, 'groupAppliances');

      plan.endBatchAction('groupAppliances', skipHistory);
    },
    addToHood: async (
      applianceOrGroup: GroupId | ApplianceId | Group | Appliance,
      hoodId: HoodId,
      isGroup: boolean,
    ) => {
      const { plan, project, planner } = get();

      if (planner.imageRendering) return;
      plan.startBatchAction('addToHood');

      try {
        if (isGroup) {
          project.addGroupToHood(applianceOrGroup as Group | GroupId, hoodId, planAction, 'addToHood');
        } else {
          await project.addToHood(applianceOrGroup as Appliance | ApplianceId, hoodId, planAction, 'addToHood');
        }
      } catch (err) {
        console.error('Failed to add item to group', err);
      } finally {
        plan.endBatchAction('addToHood');
      }
    },
    createHoodAtAppliance: async (appliance, hood) => {
      return createHoodAtApplianceHelper(appliance, hood, get().plan, get().project, get);
    },
    removeFromHood: (group) => {
      if (group.relations.hoodIds.length < 1) {
        return;
      }
      const { plan, planner } = get();

      if (planner.imageRendering) return;
      plan.startBatchAction('removeFromHood');
      set(
        (state) => {
          group.relations.hoodIds.forEach((hoodId) => {
            const hood = state.project.data.hoods[hoodId];

            if (!hood || hood.type !== 'hood') {
              console.warn(`Could not find hood (${hoodId}) referred by group ${group.id}`);
              return;
            }

            const writableGroup = state.project.data.groups[group.id];

            if (!writableGroup) {
              console.warn(`Could not find a group (${group.id}) to remove from hood`);
              return;
            }

            writableGroup.relations.hoodIds = writableGroup.relations.hoodIds.filter((hid) => hid !== hoodId);
            hood.relations.groupId = undefined;
          });
        },
        undefined,
        { type: 'plan/removeFromHood', group: group.id },
      );

      plan.endBatchAction('removeFromHood');
    },
    removeFromGroup: (appliance, newX, newY) => {
      const { plan, planner } = get();

      if (planner.imageRendering) return;

      plan.startBatchAction('removeFromGroup');

      const aid = typeof appliance === 'string' ? appliance : appliance.id;

      set(
        (state) => {
          const app = state.project.data.appliances[aid];

          if (app) {
            const groupId = app.relations.groupId;
            if (app.haltonValues.type !== 'spacer') {
              app.relations.groupId = undefined;
              app.relations.rowId = undefined;
              app.relations.rowNum = undefined;
              app.relations.hoodIds = undefined;

              if (app.airchefValues.transform.position) {
                if (app.airchefValues.offsetTransform) {
                  app.airchefValues.offsetTransform.position.x.value += 500;
                  app.airchefValues.offsetTransform.position.y.value -= 500;
                } else {
                  app.airchefValues.transform.position.x.value += 500;
                  app.airchefValues.transform.position.y.value -= 500;
                }
                if (newX !== undefined) {
                  if (app.airchefValues.offsetTransform) {
                    app.airchefValues.offsetTransform.position.x = createMeasurement(newX * 10, 'mm', 'length');
                  } else {
                    app.airchefValues.transform.position.x = createMeasurement(newX * 10, 'mm', 'length');
                  }
                }
                if (newY !== undefined) {
                  if (app.airchefValues.offsetTransform) {
                    app.airchefValues.offsetTransform.position.y = createMeasurement(newY * 10, 'mm', 'length');
                  } else {
                    app.airchefValues.transform.position.y = createMeasurement(newY * 10, 'mm', 'length');
                  }
                }
              }
            } else {
              // spacer, just remove completely
              delete state.project.data.appliances[app.id];
            }

            const groupImmutable = groupId ? get().project.data.groups[groupId] : undefined;

            if (groupImmutable) {
              let updatedGroup = new GroupUpdater(groupImmutable).removeAppliance(aid).update();

              if (updatedGroup.relations.applianceIds.length < 2) {
                // 1 or 0 appliances in the group, remove the group if it is empty, or last appliance is a spacer
                if (updatedGroup.relations.applianceIds.length === 1) {
                  // 1 asset left
                  const otherApp = state.project.data.appliances[updatedGroup.relations.applianceIds[0]];
                  if (otherApp?.haltonValues.type === 'spacer') {
                    // Spacer, just delete it
                    updatedGroup = new GroupUpdater(updatedGroup).removeAppliance(otherApp.id).update();
                    delete state.project.data.appliances[otherApp.id];
                  }
                }

                // No more appliances in the group, but it has hoods. Remove leftover hoods.
                if (updatedGroup.relations.applianceIds.length === 0 && updatedGroup.relations.hoodIds.length > 0) {
                  updatedGroup.relations.hoodIds.forEach((hoodId) => {
                    delete state.project.data.hoods[hoodId];
                  });
                }

                if (updatedGroup.relations.applianceIds.length === 0) {
                  // Delete the empty group
                  delete state.project.data.groups[updatedGroup.id];
                } else {
                  // Push the updated group into the state
                  state.project.data.groups[updatedGroup.id] = updatedGroup;
                }
              } else {
                // Push the updated group into the state
                state.project.data.groups[updatedGroup.id] = updatedGroup;
              }
            }
          }
        },
        undefined,
        { type: 'plan/removeFromGroup', appliance: aid },
      );

      plan.endBatchAction('removeFromGroup');
    },
    getGroup: (id) => {
      const { project } = get();
      const group = project.data.groups[id];
      if (!group) {
        return null;
      }
      return scaleAssetForPlanner(group);
    },
    getGroups: () => {
      const { project } = get();
      const { currentRoom } = get().planner;
      if (!currentRoom) return [];
      const list = Object.values(project.data.groups).filter((g) => g.relations.roomId === currentRoom);
      return list.map(scaleAssetForPlanner);
    },
    moveGroupAppliancesToRow: (id, appliances, rowNum) => {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;
      const group = simpleClone(project.data.groups[id]);
      if (!group) {
        throw new InvalidStateError('Group not found iwth id');
      }

      if (rowNum === 1 && group.airchefValues.hasVirtualWall === true) {
        console.warn(`Group has a virtual wall`);
        return;
      }

      const rows = group.rows
        .map((r) => r.relations.applianceIds)
        .map((row) => row.filter((aid) => !appliances.includes(aid)));

      if (rows[rowNum] === undefined) {
        rows[rowNum] = [];
      }

      rows[rowNum].push(...appliances);

      const updater = new GroupUpdater(group);
      rows.forEach((row, index) => updater.setRow(index as 0 | 1, row));
      const updatedGroup = updater.update();

      if (!updatedGroup.rows[rowNum]) {
        throw new InvalidStateError('Row was not created successfully on update!');
      }

      plan.startBatchAction('moveGroupAppliancesToRow');
      set(
        (state) => {
          state.project.data.groups[id] = updatedGroup;
          appliances.forEach((aid) => {
            state.project.data.appliances[aid].relations.groupId = id;
            state.project.data.appliances[aid].relations.rowId = updatedGroup.rows[rowNum]!.id;
            state.project.data.appliances[aid].relations.rowNum = rowNum;
          });
        },
        undefined,
        { type: 'plan/moveGroupAppliancesToRow', groupId: id, appliances, rowNum },
      );
      plan.endBatchAction('moveGroupAppliancesToRow');
    },
    updateGroup: (
      id: GroupId,
      appliances: OptionalTuple<Appliance[]>,
      hoods: OptionalTuple<Hood[]> = [],
      floatingAppliances: OptionalTuple<FloatingAppliance[]> = [],
    ) => {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;
      const allGroundAppliances = appliances.flat().filter(Boolean) as Appliance[];
      const allHoods = hoods.flat().filter(Boolean) as Hood[];
      const allFloatingAppliances = floatingAppliances
        .flat()
        .filter(Boolean)
        .map((fa) => fa?.appliance)
        .filter(Boolean) as Appliance[];

      if (allGroundAppliances.length < 1 && allHoods.length < 1) {
        return;
      }

      const roomId =
        allGroundAppliances.length > 0 ? allGroundAppliances[0].relations.roomId : allHoods[0].relations.roomId;

      if (!roomId) {
        throw new InvalidStateError('Cannot update group without a room');
      }

      project.updateAssets(
        ([] as Device[])
          .concat(allGroundAppliances)
          .concat(allFloatingAppliances)
          .concat(allHoods)
          .map(scaleAssetForStore),
        planAction,
        'updateGroup',
      );
      const oldGroup = get().project.data.groups[id];
      const updater = new GroupUpdater(oldGroup).setRoomId(roomId);
      ([0, 1] as const).forEach((rowIndex) => {
        const rowBuilder = new RowBuilder(oldGroup.rows[rowIndex]);
        if (appliances[rowIndex] !== undefined) {
          rowBuilder.setAppliances(appliances[rowIndex].map((a) => a.id));
        }
        if (hoods[rowIndex] !== undefined) {
          rowBuilder.setHoods(hoods[rowIndex].map((a) => a.id));
        }
        if (floatingAppliances[rowIndex] !== undefined) {
          rowBuilder.setFloatingAppliances(
            floatingAppliances[rowIndex].map((fa) => ({
              applianceId: fa.appliance.id,
              offset: fa.offset,
            })),
          );
        }
        const row = rowBuilder.build();
        if (!rowIsEmpty(row)) {
          updater.setRow(rowIndex, row);
        }
      });
      const updatedGroup = updater.update();
      project.updateGroup(updatedGroup, planAction, 'updateGroup');

      // Allow any effects to run an possibly update state,
      // then do undo point
      plan.endBatchAction('updateGroup');
    },
    updateGroupRowOffset(groupId, rowId, offsetCm) {
      const { planner } = get();
      if (planner.imageRendering) return;
      set(
        (state) => {
          const group = state.project.data.groups[groupId];
          if (!group) return;

          const row = group.rows.find((r) => r.id === rowId);
          if (!row) return;

          row.airchefValues.appliancesPositionOffset.x = createMeasurement(offsetCm, 'cm');
        },
        undefined,
        { type: 'plan/updateGroupRowOffset', groupId, rowId, offsetCm },
      );
    },
    reorderGroupAppliances: (groupId, rowId, applianceId, newPosition) => {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;
      const group = project.data.groups[groupId] as Group | undefined;

      if (!group) {
        return;
      }

      const appliance = project.data.appliances[applianceId];
      if (!appliance) {
        console.warn('given appliance not found');
        return;
      }

      const sourceRowIndex = group.rows.findIndex((row) => row.relations.applianceIds.includes(applianceId));
      const targetRowIndex = group.rows.findIndex((row) => row.id === rowId);

      if (sourceRowIndex < 0 || targetRowIndex < 0) {
        console.warn('source or target row not found for appliance reorder');
        return;
      }

      plan.startBatchAction('reorderGroupAppliances');

      const clone = simpleClone(group);

      const sourceRow = clone.rows[sourceRowIndex];
      const targetRow = clone.rows[targetRowIndex];
      sourceRow.relations.applianceIds = sourceRow.relations.applianceIds.filter((aid) => aid !== applianceId);

      // + 1 here to allow adding to the end of the other row, as its length will need to grow by one if
      // the row changes
      const insertIndex = Math.max(0, Math.min(newPosition, targetRow.relations.applianceIds.length + 1));
      targetRow.relations.applianceIds.splice(insertIndex, 0, applianceId);

      clone.relations.applianceIds = clone.rows.flatMap((row) => row.relations.applianceIds);

      set(
        (state) => {
          clone.rows.forEach((row, index) => {
            row.relations.applianceIds.forEach((aid) => {
              const app = state.project.data.appliances[aid];
              if (!app) {
                return;
              }

              app.relations.groupId = clone.id;
              app.relations.rowId = row.id;
              app.relations.rowNum = index;
            });
          });

          const movedAppliance = state.project.data.appliances[applianceId];
          if (movedAppliance) {
            movedAppliance.relations.groupId = clone.id;
            movedAppliance.relations.rowId = targetRow.id;
            movedAppliance.relations.rowNum = targetRowIndex;
          }

          state.project.data.groups[groupId] = clone;
        },
        undefined,
        { type: 'plan/reorderGroupAppliances', groupId, rowId, applianceId, newPosition },
      );

      plan.endBatchAction('reorderGroupAppliances');
    },
    updateFloatingApplianceOffset: (groupId, rowId, applianceId, offsetCm) => {
      const { plan, planner } = get();
      if (planner.imageRendering) return;
      plan.startBatchAction('updateFloatingApplianceOffset');
      set(
        (state) => {
          const group = state.project.data.groups[groupId];
          if (!group) {
            console.warn('Could not find group to update floating offset');
            return;
          }

          const row = group.rows.find((r) => r.id === rowId);
          if (!row) {
            console.warn('Could not find row to update floating offset');
            return;
          }

          const positioner = row.relations.floatingApplianceIds.find((ap) => ap.applianceId === applianceId);
          if (!positioner) {
            console.warn('Could not find appliance positioner to update floating offset');
            return;
          }

          positioner.offset.x = createMeasurement(offsetCm * 10, 'mm', 'length');
        },
        undefined,
        {
          type: 'plan/updateFloatingApplianceOffset',
          groupId,
          rowId,
          applianceId,
          offsetCm,
        },
      );

      plan.endBatchAction('updateFloatingApplianceOffset');
    },
    toggleApplianceFloat: (applianceId) => {
      const { plan, planner, project } = get();
      if (planner.imageRendering) return;
      const units = planner.prefs.units;
      let unit = units === 'si' ? 'm' : 'ft';
      let value = units === 'si' ? 1 : 3;

      if (!project.data.appliances[applianceId]) return;

      plan.startBatchAction('toggleApplianceFloat');

      set(
        (state) => {
          // This should always be available, barring any microsecond cosmic ray bitflips.
          // As we check it just before this callback.
          const appliance = state.project.data.appliances[applianceId];

          const groupId = appliance.relations.groupId;

          const plainGroup = groupId ? project.data.groups[groupId] : undefined;

          const rowNum = appliance.relations.rowNum ?? -1;
          const row = (plainGroup && rowNum >= 0 && rowNum <= 1 && plainGroup.rows[rowNum]) || undefined;

          let isFloating = appliance.airchefValues.transform.position.z.value > 0;
          const nonZeroHeight = isFloating;
          if (row) {
            isFloating = row.relations.floatingApplianceIds.some((ap) => ap.applianceId === applianceId);
          }

          if (nonZeroHeight && !isFloating && row) {
            // Appliance pos is already above floor, but not in the floating list of the row.
            // Preserve the existing height.
            unit = appliance.airchefValues.transform.position.z.unit;
            value = appliance.airchefValues.transform.position.z.value;
          }

          const newZPos: Measurement<'length'> = isFloating
            ? { value: 0, unit: 'mm', type: 'length' as const }
            : { value, unit, type: 'length' as const };

          appliance.airchefValues.transform.position.z = newZPos;
          updateAssetModified(appliance, state);

          if (plainGroup && row) {
            const rowBuilder = new RowBuilder(row).removeAppliance(applianceId);
            if (isFloating) {
              rowBuilder.addAppliance(applianceId);
            } else {
              rowBuilder.addFloatingAppliance(applianceId);
            }
            const updatedGroup = new GroupUpdater(plainGroup).setRow(rowNum as 0 | 1, rowBuilder.build()).update();
            state.project.data.groups[updatedGroup.id] = updatedGroup;
          }

          updateProjectModified(state);
        },
        undefined,
        { type: 'plan/toggleApplianceFloat', applianceId },
      );

      plan.endBatchAction('toggleApplianceFloat');
    },
    updateGroupTransform: (id, transform) => {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;
      const oldGroup = project.data.groups[id];
      if (!oldGroup) {
        throw new InvalidStateError(`No such group ${id}!`);
      }

      plan.startBatchAction('updateGroupTransform');

      const clone = simpleClone(oldGroup);
      clone.airchefValues.transform = transform;

      project.updateGroup(scaleAssetForStore(clone), planAction, 'updateGroupTransform');

      plan.endBatchAction('updateGroupTransform');
    },
    deleteGroup(id, rowId) {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;
      plan.startBatchAction('deleteGroup');

      if (rowId !== undefined) {
        project.removeGroupRow(id, rowId, planAction, 'deleteGroup');
      } else {
        project.removeGroup(id, true, true, planAction, 'deleteGroup');
      }

      plan.endBatchAction('deleteGroup');
    },
    getSiblingAppliances(appliance) {
      const { getAsset } = get().plan;
      const asset = getAsset(appliance, 'appliance');
      const siblings: [Appliance | null, Appliance | null] = [null, null];
      if (!asset) {
        return siblings;
      }

      const groupId = asset.relations.groupId;
      if (!groupId) {
        return siblings;
      }

      const groupAsset = get().project.data.groups[groupId];
      if (!groupAsset) {
        return siblings;
      }

      const assetIndex = groupAsset.relations.applianceIds.indexOf(asset.id);
      if (assetIndex > 0) {
        const leftSibling = getAsset(groupAsset.relations.applianceIds[assetIndex - 1], 'appliance');
        siblings[0] = leftSibling ?? null;
      }

      if (assetIndex < groupAsset.relations.applianceIds.length - 1) {
        const rightSibling = getAsset(groupAsset.relations.applianceIds[assetIndex + 1], 'appliance');
        siblings[1] = rightSibling ?? null;
      }

      return siblings;
    },
    deleteAsset: (asset) => {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;
      plan.startBatchAction('deleteAsset');
      const { device: removed, groupInfo } = project.removeAsset(asset, planAction, 'deleteAsset');
      if (groupInfo && (groupInfo.wasFirst || groupInfo.wasLast)) {
        project.cleanupRowSpacers(groupInfo.groupId, groupInfo.rowNum, groupInfo.wasFirst ? 'start' : 'end');
      }

      // Remember to call get() again here
      const { project: updatedProject } = get();
      const group = groupInfo ? updatedProject.data.groups[groupInfo?.groupId] : undefined;
      const allSpacers = group
        ? group.relations.applianceIds.every(
            (appid) => updatedProject.data.appliances[appid]?.haltonValues.type === 'spacer',
          )
        : false;
      if (group && (group.relations.applianceIds.length === 0 || allSpacers)) {
        // All appliances from the group are now removed, delete the group
        project.removeGroup(group.id, true, true, planAction, 'deleteAsset');
      }
      plan.endBatchAction('deleteAsset', !removed);
    },
    duplicateAsset: async (id) => {
      const { project, plan, planner } = get();
      if (planner.imageRendering) return;
      plan.startBatchAction('duplicateAsset');
      await project.duplicateAsset(id, true, planAction, 'duplicateAsset');

      plan.endBatchAction('duplicateAsset');
    },
    addComment: (text, x, y) => {
      const roomId = get().planner.currentRoom;
      if (!roomId) {
        return null;
      }
      const user = get().planner.user.data?.userPrincipalName ?? '';
      const now = getCurrentTimestamp();
      const comment: Comment = {
        id: generateRoomPartId(),
        content: text,
        roomId,
        datesAndAudit: {
          author: user,
          modifiedBy: user,
          createdAt: now,
          modifiedAt: now,
        },
        transform: {
          position: {
            x: createMeasurement(x * 10, 'mm'),
            y: createMeasurement(y * 10, 'mm'),
            z: createMeasurement(0, 'mm'),
          },
          rotation: {
            x: 0,
            y: 0,
            z: 0,
          },
          size: {
            width: createMeasurement(0, 'mm'),
            length: createMeasurement(0, 'mm'),
            height: createMeasurement(0, 'mm'),
          },
        },
      };
      get().plan.startBatchAction('addComment');
      set(
        (state) => {
          if (!state.project.data.comments) state.project.data.comments = [];
          state.project.data.comments.push(comment);
        },
        undefined,
        { type: 'plan/addComment' },
      );

      get().plan.endBatchAction('addComment');
      return comment;
    },
    updateComment: (id, text, px, py) => {
      if (!text && py === undefined && px === undefined) {
        return;
      }
      const comment = get().project.data.comments?.find((c) => c.id === id);
      if (!comment) {
        return;
      }

      const clone = simpleClone(comment);
      if (text) {
        clone.content = text;
      }

      if (px !== undefined && py !== undefined) {
        clone.pointTo = {
          x: createMeasurement(px * 10, 'mm'),
          y: createMeasurement(py * 10, 'mm'),
          z: createMeasurement(0, 'mm'),
        };
      }

      const user = get().planner.user.data?.userPrincipalName ?? '';
      const now = getCurrentTimestamp();
      clone.datesAndAudit.modifiedAt = now;
      clone.datesAndAudit.modifiedBy = user;
      get().plan.startBatchAction('updateComment');
      set(
        (state) => {
          state.project.data.comments = (state.project.data.comments ?? []).filter((c) => c.id !== id);
          state.project.data.comments.push(clone);
        },
        undefined,
        { type: 'plan/updateComment', text, px, py },
      );
      get().plan.endBatchAction('updateComment');
    },
    moveComment(id, x, y) {
      const comment = get().project.data.comments?.find((c) => c.id === id);
      if (!comment) {
        return;
      }
      const clone = simpleClone(comment);

      const user = get().planner.user.data?.userPrincipalName ?? '';
      const now = getCurrentTimestamp();

      clone.transform.position.x = createMeasurement(x * 10, 'mm');
      clone.transform.position.y = createMeasurement(y * 10, 'mm');

      clone.datesAndAudit.modifiedAt = now;
      clone.datesAndAudit.modifiedBy = user;

      set(
        (state) => {
          state.project.data.comments = state.project.data.comments.filter((c) => c.id !== id);
          state.project.data.comments.push(clone);
        },
        undefined,
        { type: 'plan/moveComment', x, y },
      );
    },
    removeComment: (id) => {
      get().plan.startBatchAction('removeComment');
      set(
        (state) => {
          state.project.data.comments = state.project.data.comments?.filter((c) => c.id !== id);
        },
        undefined,
        { type: 'project/removeComment', id },
      );
      get().plan.endBatchAction('removeComment');
    },
    snapshot: () => {
      return get().project.data;
    },
    batchSnapshot: null,
    createProjectUndoPoint: (snapshot) => {
      const currentState = get().project.data;
      const { actionInProgress, undoInProgress } = get().plan;
      if (actionInProgress > 0 || undoInProgress) {
        return;
      }
      const changes = diff(snapshot, currentState, {
        prefilter: (_path, key) => {
          // Do not do undo points if just the modified date has changed
          if (key === 'modifiedAt') return true;
          return false;
        },
      });
      if (!changes) {
        return;
      }
      set(
        (state) => {
          state.plan.sessionHistory.push(snapshot);
          if (state.plan.sessionHistory.length > 50) {
            state.plan.sessionHistory = state.plan.sessionHistory.slice(-50);
          }
        },
        undefined,
        { type: 'plan/createProjectUndoPoint' },
      );
    },

    startBatchAction: (sourceAction) => {
      const { plan, project } = get();

      set(
        (state) => {
          if (!plan.batchSnapshot) {
            state.plan.batchSnapshot = project.data;
          }
          state.plan.actionInProgress += 1;
        },
        undefined,
        { type: `plan/${sourceAction}/startBatchAction` },
      );
    },
    endBatchAction: async (sourceAction, skipUndo = false) => {
      // Do this insde a timeout, so any effects happening from the actual state changes are run. Such as updating asset positions
      // after grouping etc, which will propagate from the canvas to the state again.
      // This way, the final endBatchAction() call will catch all those changes and group them in a single undo action.
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          const { batchSnapshot, createProjectUndoPoint, actionInProgress: prevActionInProgress } = get().plan;
          let createUndoPoint = false;
          set(
            (state) => {
              if (state.plan.actionInProgress <= 0) {
                state.plan.actionInProgress = 0;
              } else {
                state.plan.actionInProgress -= 1;
              }

              if (state.plan.actionInProgress <= 0) {
                state.plan.batchSnapshot = null;
                createUndoPoint = true;
              }

              state.plan.dirty = true;
            },
            undefined,
            { type: `plan/${sourceAction}/endBatchAction` },
          );
          if (batchSnapshot && createUndoPoint && prevActionInProgress > 0 && !skipUndo) {
            createProjectUndoPoint(simpleClone(batchSnapshot));
          }

          resolve();
        }, BATCH_WAIT_MS);
      });
    },
    pasteAppliances: (roomId, x, y) => {
      if (!roomId) return;
      const sourceGroupId = get().planner.clipboard.group;
      if (!sourceGroupId) return;

      const group = simpleClone(get().project.data.groups[sourceGroupId] as Group | undefined);
      if (!group) return;

      const { appliances } = get().project.data;
      const rowOneAppliances = group.rows[0].relations.applianceIds
        .map((aid) => appliances[aid])
        .map((a) => simpleClone(a))
        .map((a) => ({ ...a, id: generateRoomPartId<'appliance'>() }));

      const rowTwoAppliances = group.rows[1]
        ? group.rows[1].relations.applianceIds
            .map((aid) => appliances[aid])
            .map((a) => simpleClone(a))
            .map((a) => ({ ...a, id: generateRoomPartId<'appliance'>() }))
        : undefined;

      const groupUpdater = new GroupUpdater(group)
        .setGenerateId(true)
        .setResetNumber(true)
        .setRoomId(roomId)
        .setPosition(x, y)
        .setRow(0, new RowBuilder().setAppliances(rowOneAppliances.map((a) => a.id)).build());

      if (rowTwoAppliances) {
        groupUpdater.setRow(1, new RowBuilder().setAppliances(rowTwoAppliances.map((a) => a.id)).build());
      } else {
        groupUpdater.setRow(1, 'clear');
      }

      const finalGroup = groupUpdater.update();

      if (rowTwoAppliances && !finalGroup.rows[1]) {
        throw new InvalidStateError('Appliance copy failure, got second row of appliances but no two rows in group');
      }

      rowOneAppliances.forEach((a) => {
        a.relations.roomId = roomId;
        a.relations.rowNum = 0;
        a.relations.groupId = finalGroup.id;
        a.relations.rowId = finalGroup.rows[0].id;
        a.relations.hoodIds = undefined;
        a.relations.hoodIdsAbove = undefined;
      });

      rowTwoAppliances?.forEach((a) => {
        a.relations.roomId = roomId;
        a.relations.rowNum = 1;
        a.relations.groupId = finalGroup.id;
        a.relations.rowId = finalGroup.rows[1]!.id;
        a.relations.hoodIds = undefined;
        a.relations.hoodIdsAbove = undefined;
      });

      get().plan.startBatchAction('pasteAppliances');
      set(
        (state) => {
          state.project.data.groups[finalGroup.id] = finalGroup;
          ([] as Appliance[])
            .concat(rowOneAppliances)
            .concat(rowTwoAppliances ?? [])
            .forEach((appliance) => {
              state.project.data.appliances[appliance.id] = appliance;

              updateAssetModified(state.project.data.appliances[appliance.id], state);
            });

          state.planner.clipboard.group = null;
          updateProjectModified(state);
        },
        undefined,
        { type: 'plan/pasteAppliances', roomId, x, y, groupId: sourceGroupId },
      );
      get().plan.endBatchAction('pasteAppliances');
    },
    undo: () => {
      const { sessionHistory } = get().plan;
      const newHistory = [...sessionHistory];
      const lastState = newHistory.pop();
      if (!lastState) return;

      set(
        (state) => {
          state.plan.undoInProgress = true;
          state.project.data = lastState;
          state.plan.sessionHistory = newHistory;
        },
        undefined,
        { type: 'plan/undo' },
      );

      setTimeout(() => {
        set(
          (state) => {
            state.plan.undoInProgress = false;
          },
          undefined,
          { type: 'plan/undo/finish' },
        );
      }, 0);
    },
    clear: () => {
      set(
        (state) => {
          state.plan.dirty = false;
          state.plan.sessionHistory = [];
        },
        undefined,
        { type: 'plan/clear' },
      );
    },
  },
});

function filterDraftDevices(device: Appliance | Hood) {
  return !device.airchefValues.isDraft;
}

function generateGroupIds(group: Group) {
  const g = simpleClone(group);
  g.id = generateRoomPartId();
  for (const r of g.rows) {
    r.id = generateRoomPartId();
  }

  return g;
}
